/* eslint-disable @tanstack/query/exhaustive-deps */
import { QUERY_KEYS } from '@/constants/query';
import { ERROR_MSG_EXCEED_WALLET_BALANCE, ERROR_MSG_LIMIT_PRICE_FAR_FROM_MARK } from '@/constants/simulation';
import { INVALID_PRICE } from '@/constants/trade';
import SentryService from '@/entities/SentryService';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import type { WrappedPosition } from '@/entities/WrappedPosition';
import { queryClient } from '@/global';
import { useAppDispatch } from '@/hooks';
import { useUserAddr } from '@/hooks/web3/useChain';
import { IGateValueResponse } from '@/types/balance';
import { IScaledOrderSimulation, ISimulationOrder } from '@/types/trade';
import { parsedEthersError } from '@/utils/error';
import { toWad } from '@/utils/numberUtil';
import { getAdjustTradeSide } from '@/utils/pairs';
import { calcMarginToDeposit } from '@/utils/position';
import { ZERO, type Side } from '@synfutures/sdks-perp';
import { useQuery } from '@tanstack/react-query';
import BN from 'bignumber.js';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { ITokenBalanceFromApiResponse } from '../balance/types';
import { useCurrentPairFromUrl } from '../pair/hook';
import { useSDK } from '../web3/hook';
import { simulateMarketTrade } from './actions';
import { useTradeSide, useTradeStoreTransformed } from './hooks';

export interface UseSimulateMarketTradeParams {
  chainId: number;
  base?: string;
  tradeSide: Side;
  position: WrappedPosition | undefined;
  leverage?: string;
  slippage: number;
  margin?: string;
  maxLeverage?: number;
  userAddr: string;
  enabled?: boolean;
}

export const useSimulateMarketTrade = ({
  chainId,
  base,
  tradeSide,
  position,
  leverage,
  slippage,
  margin,
  maxLeverage = 100,
  userAddr,
  enabled = true,
}: UseSimulateMarketTradeParams) => {
  const dispatch = useAppDispatch();
  const sdkContext = useSDK(chainId);
  return useQuery({
    queryKey: QUERY_KEYS.TRADE.SIMULATE_MARKET_TRADE({
      chainId,
      userAddr,
      base,
      tradeSide: tradeSide?.toString(),
      leverage,
      slippage,
      margin,
      maxLeverage,
    }),
    queryFn: async () => {
      const result = await dispatch(
        simulateMarketTrade({
          chainId,
          sdkContext,
          base,
          tradeSide,
          position,
          leverage,
          slippage,
          margin,
          maxLeverage,
          userAddr,
        }),
      ).unwrap();
      return result;
    },
    enabled: enabled && !!sdkContext && !!position && !!userAddr && !!base && !!tradeSide && chainId > 0,
    staleTime: 0, // Always refetch for real-time simulation
    refetchOnWindowFocus: false,
  });
};
export function useSimulateBatchPlaceScaledLimitOrder(chainId: number | undefined) {
  const sdkContext = useSDK(chainId);
  const userAddr = useUserAddr();
  const tradeSide = useTradeSide(chainId);
  const currentPair = useCurrentPairFromUrl(chainId);
  // const portfolio = useWrappedPortfolioByPairId(chainId, userAddr, currentPair?.id);
  // const balanceMap = useTokenBalanceMapState(chainId);
  const { t } = useTranslation();

  const {
    scaleLimitStepRatio,
    lowerTick,
    lowerPrice,
    upperPrice,
    upperTick: storeUpperTick,
    tradeSize,
    orderCount,
    sizeDistribution,
    leverage: storeLeverage,
  } = useTradeStoreTransformed();
  return useQuery({
    queryKey: QUERY_KEYS.TRADE.SIMULATE_BATCH_PLACE_SCALED_LIMIT_ORDER({
      chainId,
      userAddr,
      orderCount,
      stepRatio: scaleLimitStepRatio,
      sizeDistribution,
      upperPrice,
      lowerPrice,
      tradeSize,
      leverage: storeLeverage,
    }),
    queryFn: async (): Promise<IScaledOrderSimulation | null> => {
      const leverage = storeLeverage ? toWad(storeLeverage || 0) : undefined;

      if (!scaleLimitStepRatio || !lowerTick || !sdkContext || !chainId || !leverage || !currentPair || !userAddr) {
        return null;
      }
      const base = toWad(tradeSize || 0);

      const priceInfo = [];
      let tick = lowerTick;
      let upperTick = storeUpperTick;
      if (currentPair.isInverse) {
        const tickFromLower = tick * -1;
        const tickFromUpper = upperTick * -1;
        tick = Math.min(tickFromLower, tickFromUpper);
        upperTick = Math.max(tickFromLower, tickFromLower);
      }
      while (tick <= upperTick) {
        priceInfo.push(tick);
        tick = tick + scaleLimitStepRatio;
      }

      try {
        if (
          !!priceInfo.length &&
          !currentPair.wrapAttribute('fairPrice').eq(lowerPrice) &&
          !base.eq(0) &&
          !leverage.eq(0)
        ) {
          if (currentPair) {
            const rootInstrument = currentPair.rootInstrument;

            const adjustTradeSide = getAdjustTradeSide(tradeSide, rootInstrument.isInverse);

            const gateBalanceList =
              queryClient.getQueryData<IGateValueResponse | null>(QUERY_KEYS.BALANCE.GATE(chainId, userAddr))
                ?.portfolios || [];
            const gateBalanceInfo = (gateBalanceList || [])?.find(
              (balance) => balance.address === rootInstrument.quoteToken.address,
            );

            const result = await sdkContext.perp.simulate.simulateScaledLimitOrder({
              instrument: rootInstrument,
              leverage: leverage,
              side: adjustTradeSide,
              sizeDistribution: sizeDistribution,
              size: {
                base: base,
              },
              priceInfo,
              tradeInfo: {
                instrumentAddr: currentPair.rootInstrument.instrumentAddr,
                expiry: currentPair.expiry,
                traderAddr: userAddr,
              },
              isInverse: currentPair.isInverse,
            });
            const orders: ISimulationOrder[] = result.orders.filter((o) => !!o) as ISimulationOrder[];
            const totalMargin = orders.reduce((acc, cur) => acc.add(cur?.margin || 0), WrappedBigNumber.ZERO);
            const marginToDeposit = calcMarginToDeposit(totalMargin, gateBalanceInfo?.balance || 0);
            const simulateResult: IScaledOrderSimulation = {
              data: {
                ...result,
                sizeDistribution,
                leverageWad: leverage,
                lowerTick: lowerTick,
                upperTick: upperTick,
                upperPrice: upperPrice,
                lowerPrice: lowerPrice,
                baseSize: base,
                orders,
                margin: totalMargin,
                marginToDeposit: marginToDeposit,
                minOrderValue: WrappedBigNumber.from(rootInstrument.minOrderValue),
                minFeeRebate: orders.reduce((acc, cur) => acc.add(cur.minFeeRebate), ZERO),
                estimatedTradeValue: orders.reduce((acc, cur) => acc.add(cur.tradeValue), WrappedBigNumber.ZERO),
              },
            };
            const minAmount = WrappedBigNumber.from(simulateResult?.data?.totalMinSize || 0);
            if (minAmount.gt(tradeSize)) {
              simulateResult.message = t('common.tradePage.lessThanMinOrder', {
                orderValue: minAmount.formatDisplayNumber({
                  roundingMode: BN.ROUND_UP,
                }),
                base: currentPair?.rootInstrument.baseToken.symbol,
              });
            }
            // check if the margin is not enough
            // if (userAddr && simulateResult.data?.marginToDeposit?.gt(0)) {
            //   const balance = _.get(balanceMap, [userAddr, rootInstrument.quoteToken.address]);
            //   if (balance) {
            //     if (simulateResult.data.marginToDeposit.gt(balance.balance)) {
            //       simulateResult.message = ERROR_MSG_EXCEED_WALLET_BALANCE.errorMsg;
            //       simulateResult.errorData = ERROR_MSG_EXCEED_WALLET_BALANCE.errorData;
            //     }
            //   }
            // }
            if (userAddr && simulateResult.data?.marginToDeposit?.gt(0)) {
              const balances = queryClient.getQueryData<ITokenBalanceFromApiResponse>(
                QUERY_KEYS.BALANCE.TOKEN_BALANCE(chainId, userAddr),
              );
              const balance = balances?.find(
                (b) => b.address.toLowerCase() === rootInstrument.quoteToken.address.toLowerCase(),
              );
              if (balance) {
                if (simulateResult.data.marginToDeposit.gt(balance?.balance || 0)) {
                  simulateResult.message = ERROR_MSG_EXCEED_WALLET_BALANCE.errorMsg;
                  simulateResult.errorData = ERROR_MSG_EXCEED_WALLET_BALANCE.errorData;
                }
              }
            }

            return simulateResult;
          }
        }
        return null;
      } catch (e) {
        SentryService.captureException(
          e,
          {
            name: 'tradeForm/simulateBatchPlaceScaledLimitOrder',
            tradeSide,
            chainId,
          },
          'info',
        );
        console.log('🚀 scaleOrderSimulate error', e);
        const parsedError = parsedEthersError(e);
        const errorMsg = parsedError?.errorMsg || _.get(e, ['reason']) || _.get(e, ['message']);
        console.log('🚀 ~ file: actions.ts:546 ~ errorMsg:', errorMsg, parsedError);
        if (errorMsg.startsWith(ERROR_MSG_LIMIT_PRICE_FAR_FROM_MARK) || errorMsg.includes(INVALID_PRICE)) {
          return null;
        }
        return { message: errorMsg, errorData: parsedError?.errorData };
      }
    },
  });
}
